import { removeSlug } from '@cobalt/utilities';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { loadFromAPIs } from '@utilities/fetch';
import { Warn } from '@utilities/logging';
import { parseHtml } from '@utilities/parsing';

export type RoutedMenuPluginOptions = {
  targetSelector: string;
  bannerSelector?: string;
  activeMenuItemClass?: string;
  contentLibrary?: string;
  scrollToTop?: boolean;
  inlineAlignment?: ScrollIntoViewOptions['inline'];
};

const defaults: RoutedMenuPluginOptions = {
  targetSelector: '.js-menu-target',
  bannerSelector: '#match-banner',
  inlineAlignment: 'center',
};

const loadingSpinner = parseHtml(`
<div class="loading-overlay" pk-theme="dark">
  <div class="loading-overlay__overlay"></div>
  <pk-spinner class="loading-overlay__spinner" size="70"></pk-spinner>
</div>`);

export default class RoutedMenuPlugin {
  dom: HTMLPkTabsElement | HTMLPkSegmentedControlElement | HTMLElement;
  opt: RoutedMenuPluginOptions;
  lastTargetPath: string;
  menuItems: (HTMLLinkElement | HTMLPkMenuItemElement)[];

  abortController: AbortController;

  constructor(e: HTMLElement, o: RoutedMenuPluginOptions) {
    this.dom = e;
    this.opt = { ...defaults, ...o };

    if (this.isTabMenu && this.hasMoreMenu) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 5000);
    }
  }

  init() {
    if (this.enableContentLoad && !this.target) {
      Warn(`RoutedMenuPlugin: no target element found: ${this.opt.targetSelector}`);
      return;
    }

    window.addEventListener('popstate', this.onPopState);

    this.menuItems = Array.from(this.dom.querySelectorAll('.js-menu-item'));
    this.menuItems.forEach((item) => {
      item.addEventListener('click', (e) => this.onMenuClick(e));
    });

    this.setActiveMenu(location.pathname);
    this.pageNameCache.set(location.pathname, GoogleTagManagerHelper.getFromDataLayer('pageName'));
  }

  get enableContentLoad() {
    return !!this.opt.targetSelector;
  }

  get banner() {
    const el = document.querySelector<HTMLElement>(`${this.opt.bannerSelector} .promoLibrary`);
    return el && window.getComputedStyle(el).display !== 'none' ? el : null;
  }

  get target() {
    return document.querySelector<HTMLElement>(this.opt.targetSelector);
  }

  get isTabMenu() {
    return this.dom.tagName === 'PK-TABS';
  }

  get isSegmentedControlMenu() {
    return this.dom.tagName === 'PK-SEGMENTED-CONTROL';
  }

  get hasMoreMenu() {
    return this.isTabMenu && !!this.dom.querySelector('pk-dropdown[slot="more"]');
  }

  displayMatchBanner() {
    if (!this.banner) return;

    const id = this.banner.id;

    googletag.cmd.push(function () {
      const slot = googletag
        .pubads()
        .getSlots()
        .find((s) => s.getSlotElementId() === id);
      if (slot) {
        googletag.pubads().refresh([slot], { changeCorrelator: false });
      }
    });
  }

  pageNameCache = new Map<string, string>();

  onMenuClick = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    const el = event.currentTarget as HTMLLinkElement;
    const targetPath = el.href || el.dataset.loadTarget;

    const pagemeta = el.dataset.pagemeta ? (JSON.parse(el.dataset.pagemeta) as Record<string, string>) : null;
    this.updatePageContent(targetPath, pagemeta);
    return false;
  };

  updatePageContent = async (targetPath: string, pagemeta: Record<string, string>) => {
    if (!targetPath) return;

    const addContentLibrary = this.opt.contentLibrary && !targetPath.endsWith(`/${this.opt.contentLibrary}`);
    const path = addContentLibrary ? [targetPath, this.opt.contentLibrary].join('/').replace('//', '/') : targetPath;

    if (this.lastTargetPath === path) return;
    this.lastTargetPath = path;

    let content: HTMLElement;
    if (this.enableContentLoad) {
      content = await this.loadContent(path);

      if (!content) return;
    }

    const virtPath = this.getPageUrl(path).pathname + location.search;

    history.pushState(null, '', virtPath);

    if (pagemeta) {
      pagemeta['virtPath'] = pagemeta.pageName;
      GoogleTagManagerHelper.addPageMeta(pagemeta);
      GoogleTagManagerHelper.addVirtualPage(pagemeta);
    }

    if (this.enableContentLoad) {
      this.target.replaceWith(content);
    }
    this.setActiveMenu(path);

    loadingSpinner.remove();

    this.displayMatchBanner();
  };

  loadContent = async (path: string) => {
    if (this.abortController) {
      this.abortController.abort();
      delete this.abortController;
    }

    this.target.classList.add('pk-position--relative');
    if (this.banner) {
      loadingSpinner.classList.add('loading_overlay_matchcentre');
    }
    this.target.appendChild(loadingSpinner);

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    let response: string;
    try {
      response = await loadFromAPIs<string>(path, {}, { responseType: 'text', signal });
    } catch (e) {
      if (e.name === 'AbortError' || signal.aborted) return;
      Warn(e);
    }

    loadingSpinner.remove();

    response = response?.trim();

    if (!response) return;
    const parsedContent = parseHtml(response, 'text/html', true);
    return parsedContent.querySelector<HTMLElement>(this.opt.targetSelector) ?? parsedContent;
  };

  onPopState = () => {
    const isHashChange = location.hash !== '';

    if (isHashChange) return;

    this.updatePageContent(location.pathname, null);
  };

  setActiveMenu = (path: string) => {
    const url = this.getPageUrl(path);
    const activeIndex = this.menuItems.findIndex(
      (item) => removeSlug(item.href) === removeSlug(url.href) || removeSlug(item.href) === removeSlug(url.pathname),
    );
    if (activeIndex === -1) return;

    this.menuItems.forEach((item, i) => {
      const active = i === activeIndex;

      if (this.opt.activeMenuItemClass) {
        active && item.classList.add(...this.opt.activeMenuItemClass.split(' '));
        !active && item.classList.remove(...this.opt.activeMenuItemClass.split(' '));
      }

      if (this.isTabMenu) {
        (item.closest('pk-tab') ?? (item as HTMLPkMenuItemElement)).selected = active;
        (item as HTMLPkMenuItemElement).highlight = active;
      } else if (this.isSegmentedControlMenu) {
        const el = item.closest('pk-segmented-control-item') ?? (item as HTMLPkSegmentedControlItemElement);
        el.active = active;
        if (active) {
          (this.dom as HTMLPkSegmentedControlElement).value = el.value;
        }
      } else {
        item.querySelectorAll('pk-chip').forEach((chip) => (chip.selected = active));
      }

      if (!active) return;

      if (this.opt.scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (!this.hasMoreMenu) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: this.opt.inlineAlignment });
      }
    });
  };

  getPageUrl = (path: string) => new URL(path.replace(`/${this.opt.contentLibrary}`, '/'), location.origin);
}
